import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, UserX, UserCheck, Trash2, Pause, Play, ExternalLink, Download, Crown, X, User as UserIcon } from "lucide-react";
import { GlassCard, PillButton } from "./_shared";

const ROLE_COLOR = {
  super_admin: "bg-orange-500/15 text-orange-300 border-orange-400/40",
  admin_entreprise: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  manager: "bg-sky-500/100/20 text-sky-200 border-sky-400/40",
  commercial: "bg-emerald-500/100/20 text-emerald-300 border-emerald-400/40",
  viewer: "bg-[var(--surface-alt)]0/20 text-zinc-200 border-slate-400/40",
};
const PLAN_COLOR = {
  Gratuit: "bg-[var(--surface-alt)]0/15 text-zinc-300 border-slate-400/30",
  Indépendant: "bg-[var(--surface-alt)] text-zinc-300 border-[var(--border)]",
  Entreprise: "bg-amber-500/10 text-amber-200 border-amber-500",
};

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/users?limit=500");
    setUsers(data);
  };
  useEffect(() => {
    load();
  }, []);

  const openUser = async (u) => {
    setSelected(u);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}`);
      setDetail(data);
    } catch {
      toast.error("Impossible de charger le détail");
    }
  };

  const updateField = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.put(`/admin/users/${selected.user_id}`, patch);
      toast.success("Modifié");
      setSelected(data.user);
      setDetail((d) => d ? { ...d, user: data.user } : d);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const assignRole = async (role) => {
    if (!selected) return;
    try {
      const { data } = await api.put(`/admin/users/${selected.user_id}/role`, { role });
      toast.success(`Rôle : ${role}`);
      setSelected(data.user);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const suspend = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/users/${selected.user_id}/suspend`);
      toast.success("Suspendu");
      setSelected({ ...selected, suspended: true });
      load();
    } catch { toast.error("Erreur"); }
  };
  const reactivate = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/users/${selected.user_id}/reactivate`);
      toast.success("Réactivé");
      setSelected({ ...selected, suspended: false });
      load();
    } catch { toast.error("Erreur"); }
  };
  const hardDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Supprimer définitivement ${selected.email} et toutes ses données ? (RGPD — irréversible)`)) return;
    try {
      await api.delete(`/admin/users/${selected.user_id}`);
      toast.success("Utilisateur supprimé");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const rgpdExport = async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/admin/rgpd/export/${selected.user_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rgpd-${selected.email}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Données exportées");
    } catch { toast.error("Erreur export"); }
  };

  const filtered = useMemo(() => {
    if (!users) return null;
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.plan || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  if (!users) return <div className="text-zinc-400 text-sm">Chargement…</div>;

  return (
    <div className="space-y-4">
      <GlassCard className="p-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher email, nom, plan…"
            className="flex-1 bg-transparent text-sm text-[var(--text)] placeholder:text-zinc-400 outline-none"
            data-testid="user-search"
          />
          <span className="text-xs text-zinc-400 tabular-nums">{filtered.length}/{users.length}</span>
        </div>
      </GlassCard>

      <GlassCard className="divide-y divide-white/5">
        {filtered.map((u) => (
          <button
            key={u.user_id}
            onClick={() => openUser(u)}
            data-testid={`user-row-${u.user_id}`}
            className="w-full text-left p-3 flex items-center gap-3 hover:bg-white/[0.03] active:bg-white"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-black to-black grid place-items-center shrink-0">
              {u.is_admin ? <Crown className="w-4 h-4 text-[var(--text)]" /> : <UserIcon className="w-4 h-4 text-[var(--text)]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[var(--text)] truncate flex items-center gap-2">
                {u.name || "—"}
                {u.suspended && <span className="text-[10px] bg-rose-500/100/20 text-rose-300 border border-rose-500 rounded-full px-1.5">Suspendu</span>}
              </div>
              <div className="text-[11px] text-zinc-400 truncate">{u.email}</div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className={`text-[10px] rounded-full px-2 py-0.5 border ${PLAN_COLOR[u.plan] || PLAN_COLOR.Gratuit}`}>{u.plan || "—"}</span>
              {u.role && <span className={`text-[9px] rounded-full px-2 py-0.5 border ${ROLE_COLOR[u.role] || ROLE_COLOR.commercial}`}>{u.role}</span>}
            </div>
          </button>
        ))}
      </GlassCard>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={(e) => e.target === e.currentTarget && setSelected(null)}
          data-testid="user-edit-sheet"
        >
          <div className="w-full max-w-lg bg-[#13131a] border border-[var(--border)] rounded-t-3xl sm:border p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display font-bold text-[var(--text)] text-lg">{selected.name || "—"}</div>
                <div className="text-xs text-zinc-400">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-[var(--text)]" data-testid="close-user">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detail && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-[var(--surface)] rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-zinc-400 uppercase">Leads</div>
                  <div className="font-display font-bold text-[var(--text)] text-lg tabular-nums">{detail.stats.prospects}</div>
                </div>
                <div className="bg-[var(--surface)] rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-zinc-400 uppercase">Interactions</div>
                  <div className="font-display font-bold text-[var(--text)] text-lg tabular-nums">{detail.stats.interactions}</div>
                </div>
                <div className="bg-[var(--surface)] rounded-xl p-2.5 text-center">
                  <div className="text-[10px] text-zinc-400 uppercase">Campagnes</div>
                  <div className="font-display font-bold text-[var(--text)] text-lg tabular-nums">{detail.stats.campaigns}</div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-zinc-400 mb-1.5">Nom</div>
                <input
                  defaultValue={selected.name || ""}
                  onBlur={(e) => e.target.value !== selected.name && updateField({ name: e.target.value })}
                  className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none focus:border-black"
                  data-testid="edit-name"
                />
              </div>
              <div>
                <div className="text-xs font-semibold text-zinc-400 mb-1.5">Plan</div>
                <div className="flex gap-2">
                  {["Gratuit", "Indépendant", "Entreprise"].map((p) => (
                    <PillButton
                      key={p}
                      onClick={() => updateField({ plan: p })}
                      variant={selected.plan === p ? "primary" : "default"}
                      testId={`plan-${p}`}
                    >
                      {p}
                    </PillButton>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-zinc-400 mb-1.5">Rôle</div>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(ROLE_COLOR).map((r) => (
                    <PillButton
                      key={r}
                      onClick={() => assignRole(r)}
                      variant={selected.role === r ? "primary" : "default"}
                      testId={`role-${r}`}
                    >
                      {r}
                    </PillButton>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-[var(--border)] flex flex-wrap gap-2">
              {selected.suspended ? (
                <PillButton onClick={reactivate} variant="success" testId="reactivate-btn">
                  <Play className="w-3 h-3 inline mr-1" /> Réactiver
                </PillButton>
              ) : (
                <PillButton onClick={suspend} testId="suspend-btn">
                  <Pause className="w-3 h-3 inline mr-1" /> Suspendre
                </PillButton>
              )}
              <PillButton onClick={rgpdExport} testId="rgpd-export-btn">
                <Download className="w-3 h-3 inline mr-1" /> Export RGPD
              </PillButton>
              {selected.email !== "admin@prosperr.xyz" && (
                <PillButton onClick={hardDelete} variant="danger" testId="delete-btn" className="ml-auto">
                  <Trash2 className="w-3 h-3 inline mr-1" /> Supprimer
                </PillButton>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
